import { isInsideCodeSpan } from "../patterns";
import { withCachedMarkdownDocLines } from "../markdown-doc-cache";

export type MarkdownRichInlineKind = "bold" | "italic" | "code" | "footnote-ref";

export interface MarkdownRichInlineMatch {
    kind: MarkdownRichInlineKind;
    fromIndex: number;
    toIndex: number;
    raw: string;
    text: string;
    id?: string;
}

export type MarkdownRichBlockKind =
    | "frontmatter"
    | "definition-list"
    | "blockquote"
    | "table"
    | "footnote-definition";

export interface MarkdownRichBlockRange {
    kind: MarkdownRichBlockKind;
    startLine: number;
    endLine: number;
    fromIndex: number;
    toIndex: number;
    raw: string;
    title: string;
    body: string;
    items?: string[];
    table?: {
        headers: string[];
        rows: string[][];
        alignments: Array<"left" | "center" | "right" | null>;
    };
}

interface MarkdownDocLike {
    lines: number;
    line(number: number): { text: string };
}

function isEscaped(text: string, index: number): boolean {
    let slashCount = 0;
    for (let i = index - 1; i >= 0 && text[i] === "\\"; i -= 1) {
        slashCount += 1;
    }
    return slashCount % 2 === 1;
}

function rangesOverlap(
    left: { fromIndex: number; toIndex: number },
    right: { fromIndex: number; toIndex: number },
): boolean {
    return left.fromIndex < right.toIndex && right.fromIndex < left.toIndex;
}

function rangeOverlapsAny(
    range: { fromIndex: number; toIndex: number },
    ranges: readonly { fromIndex: number; toIndex: number }[],
): boolean {
    return ranges.some((existing) => rangesOverlap(range, existing));
}

function canUseInlineCandidate(
    text: string,
    range: { fromIndex: number; toIndex: number },
    existing: readonly MarkdownRichInlineMatch[],
): boolean {
    if (range.fromIndex >= range.toIndex) return false;
    if (isInsideCodeSpan(text, range.fromIndex)) return false;
    return !rangeOverlapsAny(range, existing);
}

function canUseEmphasisContent(content: string): boolean {
    return content.trim().length > 0 &&
        !content.startsWith(" ") &&
        !content.endsWith(" ");
}

function isWordChar(value: string | undefined): boolean {
    return !!value && /[A-Za-z0-9_]/.test(value);
}

function findClosingMarker(
    text: string,
    openIndex: number,
    marker: string,
): number {
    for (let i = openIndex + marker.length; i < text.length; i += 1) {
        if (text.startsWith(marker, i) && !isEscaped(text, i)) return i;
    }
    return -1;
}

function pushCodeMatches(
    text: string,
    matches: MarkdownRichInlineMatch[],
): void {
    for (let i = 0; i < text.length; i += 1) {
        if (text[i] !== "`" || isEscaped(text, i)) continue;
        let tickCount = 1;
        while (text[i + tickCount] === "`") tickCount += 1;
        const marker = "`".repeat(tickCount);
        let closeIndex = -1;
        for (let j = i + tickCount; j < text.length; j += 1) {
            if (text.startsWith(marker, j) && !isEscaped(text, j)) {
                closeIndex = j;
                break;
            }
        }
        if (closeIndex < 0) {
            i += tickCount - 1;
            continue;
        }
        const content = text.slice(i + tickCount, closeIndex);
        if (!content) {
            i = closeIndex + tickCount - 1;
            continue;
        }
        const candidate = {
            fromIndex: i,
            toIndex: closeIndex + tickCount,
        };
        if (rangeOverlapsAny(candidate, matches)) {
            i = closeIndex + tickCount - 1;
            continue;
        }
        matches.push({
            ...candidate,
            kind: "code",
            raw: text.slice(candidate.fromIndex, candidate.toIndex),
            text: content,
        });
        i = closeIndex + tickCount - 1;
    }
}

function pushBoldMatches(
    text: string,
    matches: MarkdownRichInlineMatch[],
): void {
    for (const marker of ["**", "__"]) {
        for (let i = 0; i < text.length; i += 1) {
            if (!text.startsWith(marker, i) || isEscaped(text, i)) continue;
            if (isInsideCodeSpan(text, i)) continue;
            const closeIndex = findClosingMarker(text, i, marker);
            if (closeIndex < 0) continue;
            const content = text.slice(i + marker.length, closeIndex);
            if (!canUseEmphasisContent(content)) continue;
            const candidate = {
                fromIndex: i,
                toIndex: closeIndex + marker.length,
            };
            if (!canUseInlineCandidate(text, candidate, matches)) continue;
            matches.push({
                ...candidate,
                kind: "bold",
                raw: text.slice(candidate.fromIndex, candidate.toIndex),
                text: content,
            });
            i = closeIndex + marker.length - 1;
        }
    }
}

function pushItalicMatches(
    text: string,
    matches: MarkdownRichInlineMatch[],
): void {
    for (const marker of ["*", "_"]) {
        for (let i = 0; i < text.length; i += 1) {
            if (text[i] !== marker || isEscaped(text, i)) continue;
            if (text[i - 1] === marker || text[i + 1] === marker) continue;
            if (marker === "_" && (isWordChar(text[i - 1]) || isWordChar(text[i + 1]))) continue;
            if (isInsideCodeSpan(text, i)) continue;

            let closeIndex = -1;
            for (let j = i + 1; j < text.length; j += 1) {
                if (text[j] !== marker || isEscaped(text, j)) continue;
                if (text[j - 1] === marker || text[j + 1] === marker) continue;
                if (marker === "_" && (isWordChar(text[j - 1]) || isWordChar(text[j + 1]))) continue;
                closeIndex = j;
                break;
            }
            if (closeIndex < 0) continue;

            const content = text.slice(i + 1, closeIndex);
            if (!canUseEmphasisContent(content)) continue;
            const candidate = { fromIndex: i, toIndex: closeIndex + 1 };
            if (!canUseInlineCandidate(text, candidate, matches)) continue;
            matches.push({
                ...candidate,
                kind: "italic",
                raw: text.slice(candidate.fromIndex, candidate.toIndex),
                text: content,
            });
            i = closeIndex;
        }
    }
}

function pushFootnoteReferenceMatches(
    text: string,
    matches: MarkdownRichInlineMatch[],
): void {
    const FOOTNOTE_REF_RE = /\[\^([^\]\s]+)]/g;
    let match: RegExpExecArray | null;
    while ((match = FOOTNOTE_REF_RE.exec(text)) !== null) {
        if (isEscaped(text, match.index) || isInsideCodeSpan(text, match.index)) continue;
        const id = match[1] ?? "";
        const candidate = {
            fromIndex: match.index,
            toIndex: match.index + match[0].length,
        };
        if (!id || !canUseInlineCandidate(text, candidate, matches)) continue;
        matches.push({
            ...candidate,
            kind: "footnote-ref",
            raw: match[0],
            text: id,
            id,
        });
    }
}

export function findMarkdownRichInlineMatches(
    text: string,
): MarkdownRichInlineMatch[] {
    const matches: MarkdownRichInlineMatch[] = [];
    pushCodeMatches(text, matches);
    pushFootnoteReferenceMatches(text, matches);
    pushBoldMatches(text, matches);
    pushItalicMatches(text, matches);
    return matches.sort((left, right) =>
        left.fromIndex - right.fromIndex || left.toIndex - right.toIndex,
    );
}

function isOccupied(
    lineNumber: number,
    excludedLines: ReadonlySet<number>,
    occupiedLines: ReadonlySet<number>,
): boolean {
    return excludedLines.has(lineNumber) || occupiedLines.has(lineNumber);
}

function lineLeadingSpaces(text: string): number {
    return text.match(/^\s*/)?.[0].length ?? 0;
}

function markOccupied(
    occupiedLines: Set<number>,
    startLine: number,
    endLine: number,
): void {
    for (let lineNumber = startLine; lineNumber <= endLine; lineNumber += 1) {
        occupiedLines.add(lineNumber);
    }
}

function parseFrontmatterKeys(lines: string[]): string[] {
    const keys: string[] = [];
    for (const line of lines) {
        const yaml = line.match(/^\s*([A-Za-z0-9_.-]+)\s*:/);
        const toml = line.match(/^\s*([A-Za-z0-9_.-]+)\s*=/);
        const key = yaml?.[1] ?? toml?.[1];
        if (key && !keys.includes(key)) keys.push(key);
    }
    return keys;
}

export function collectMarkdownFrontmatterRange(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number> = new Set(),
): MarkdownRichBlockRange | null {
    doc = withCachedMarkdownDocLines(doc);
    if (doc.lines < 3 || excludedLines.has(1)) return null;
    const firstLine = doc.line(1);
    const marker = firstLine.text.trim();
    if (marker !== "---" && marker !== "+++") return null;

    for (let lineNumber = 2; lineNumber <= doc.lines; lineNumber += 1) {
        if (excludedLines.has(lineNumber)) return null;
        if (doc.line(lineNumber).text.trim() !== marker) continue;

        const bodyLines: string[] = [];
        for (let bodyLine = 2; bodyLine < lineNumber; bodyLine += 1) {
            bodyLines.push(doc.line(bodyLine).text);
        }
        const keys = parseFrontmatterKeys(bodyLines);
        return {
            kind: "frontmatter",
            startLine: 1,
            endLine: lineNumber,
            fromIndex: lineLeadingSpaces(firstLine.text),
            toIndex: marker.length,
            raw: [firstLine.text, ...bodyLines, doc.line(lineNumber).text].join("\n"),
            title: marker === "+++" ? "TOML Frontmatter" : "YAML Frontmatter",
            body: bodyLines.join("\n").trim(),
            items: keys,
        };
    }

    return null;
}

function isDefinitionLine(text: string): boolean {
    return /^ {0,3}:(?:\s+|$)(.*)$/.test(text);
}

function canStartDefinitionTerm(text: string): boolean {
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (/^(#{1,6}|>|[-+*]\s+|\d+[.)]\s+|\|)/.test(trimmed)) return false;
    if (/^\[\^[^\]]+]:/.test(trimmed)) return false;
    if (/^[-*_]{3,}$/.test(trimmed)) return false;
    return !/^:/.test(trimmed);
}

function collectDefinitionListRanges(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number>,
    occupiedLines: Set<number>,
): MarkdownRichBlockRange[] {
    const ranges: MarkdownRichBlockRange[] = [];
    for (let lineNumber = 1; lineNumber < doc.lines; lineNumber += 1) {
        if (isOccupied(lineNumber, excludedLines, occupiedLines)) continue;
        const termLine = doc.line(lineNumber);
        if (!canStartDefinitionTerm(termLine.text)) continue;
        const nextLine = lineNumber + 1;
        if (isOccupied(nextLine, excludedLines, occupiedLines)) continue;
        if (!isDefinitionLine(doc.line(nextLine).text)) continue;

        const definitions: string[] = [];
        let endLine = nextLine;
        for (let cursor = nextLine; cursor <= doc.lines; cursor += 1) {
            if (isOccupied(cursor, excludedLines, occupiedLines)) break;
            const text = doc.line(cursor).text;
            if (isDefinitionLine(text)) {
                definitions.push(text.replace(/^ {0,3}:(?:\s+|$)/, "").trim());
                endLine = cursor;
                continue;
            }
            if (/^(?: {4,}|\t)/.test(text)) {
                definitions.push(text.trim());
                endLine = cursor;
                continue;
            }
            break;
        }

        const rawLines: string[] = [];
        for (let cursor = lineNumber; cursor <= endLine; cursor += 1) {
            rawLines.push(doc.line(cursor).text);
        }
        ranges.push({
            kind: "definition-list",
            startLine: lineNumber,
            endLine,
            fromIndex: lineLeadingSpaces(termLine.text),
            toIndex: termLine.text.length,
            raw: rawLines.join("\n"),
            title: termLine.text.trim(),
            body: definitions.filter(Boolean).join(" "),
            items: definitions.filter(Boolean),
        });
        markOccupied(occupiedLines, lineNumber, endLine);
        lineNumber = endLine;
    }
    return ranges;
}

function splitTableRow(text: string): string[] {
    const trimmed = text.trim().replace(/^\|/, "").replace(/\|$/, "");
    const cells: string[] = [];
    let current = "";
    let escaped = false;
    for (const char of trimmed) {
        if (escaped) {
            current += char;
            escaped = false;
            continue;
        }
        if (char === "\\") {
            escaped = true;
            current += char;
            continue;
        }
        if (char === "|") {
            cells.push(current.trim());
            current = "";
            continue;
        }
        current += char;
    }
    cells.push(current.trim());
    return cells;
}

function parseTableAlignment(cell: string): "left" | "center" | "right" | null {
    const trimmed = cell.trim();
    if (!/^:?-{3,}:?$/.test(trimmed)) return null;
    const left = trimmed.startsWith(":");
    const right = trimmed.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return null;
}

function collectTableRanges(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number>,
    occupiedLines: Set<number>,
): MarkdownRichBlockRange[] {
    const ranges: MarkdownRichBlockRange[] = [];
    for (let lineNumber = 1; lineNumber < doc.lines; lineNumber += 1) {
        if (isOccupied(lineNumber, excludedLines, occupiedLines)) continue;
        const headerLine = doc.line(lineNumber);
        const separatorLineNumber = lineNumber + 1;
        if (isOccupied(separatorLineNumber, excludedLines, occupiedLines)) continue;
        if (!headerLine.text.includes("|")) continue;

        const headers = splitTableRow(headerLine.text);
        const separator = splitTableRow(doc.line(separatorLineNumber).text);
        if (headers.length < 2 || separator.length !== headers.length) continue;
        if (!separator.every((cell) => /^:?-{3,}:?$/.test(cell.trim()))) continue;

        const rows: string[][] = [];
        let endLine = separatorLineNumber;
        for (let cursor = separatorLineNumber + 1; cursor <= doc.lines; cursor += 1) {
            if (isOccupied(cursor, excludedLines, occupiedLines)) break;
            const rowText = doc.line(cursor).text;
            if (!rowText.includes("|") || !rowText.trim()) break;
            const cells = splitTableRow(rowText);
            rows.push(headers.map((_, index) => cells[index] ?? ""));
            endLine = cursor;
        }

        const rawLines: string[] = [];
        for (let cursor = lineNumber; cursor <= endLine; cursor += 1) {
            rawLines.push(doc.line(cursor).text);
        }
        ranges.push({
            kind: "table",
            startLine: lineNumber,
            endLine,
            fromIndex: lineLeadingSpaces(headerLine.text),
            toIndex: headerLine.text.length,
            raw: rawLines.join("\n"),
            title: `${headers.length} columns`,
            body: `${rows.length} rows`,
            table: {
                headers,
                rows,
                alignments: separator.map(parseTableAlignment),
            },
        });
        markOccupied(occupiedLines, lineNumber, endLine);
        lineNumber = endLine;
    }
    return ranges;
}

function collectBlockquoteRanges(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number>,
    occupiedLines: Set<number>,
): MarkdownRichBlockRange[] {
    const ranges: MarkdownRichBlockRange[] = [];
    for (let lineNumber = 1; lineNumber <= doc.lines; lineNumber += 1) {
        if (isOccupied(lineNumber, excludedLines, occupiedLines)) continue;
        const line = doc.line(lineNumber);
        if (!/^ {0,3}> ?/.test(line.text)) continue;

        const lines: string[] = [];
        let endLine = lineNumber;
        for (let cursor = lineNumber; cursor <= doc.lines; cursor += 1) {
            if (isOccupied(cursor, excludedLines, occupiedLines)) break;
            const text = doc.line(cursor).text;
            if (!/^ {0,3}> ?/.test(text)) break;
            lines.push(text.replace(/^ {0,3}> ?/, ""));
            endLine = cursor;
        }
        ranges.push({
            kind: "blockquote",
            startLine: lineNumber,
            endLine,
            fromIndex: lineLeadingSpaces(line.text),
            toIndex: line.text.length,
            raw: lines.join("\n"),
            title: "Blockquote",
            body: lines.join(" ").trim(),
            items: lines,
        });
        markOccupied(occupiedLines, lineNumber, endLine);
        lineNumber = endLine;
    }
    return ranges;
}

function collectFootnoteDefinitionRanges(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number>,
    occupiedLines: Set<number>,
): MarkdownRichBlockRange[] {
    const ranges: MarkdownRichBlockRange[] = [];
    for (let lineNumber = 1; lineNumber <= doc.lines; lineNumber += 1) {
        if (isOccupied(lineNumber, excludedLines, occupiedLines)) continue;
        const line = doc.line(lineNumber);
        const match = line.text.match(/^ {0,3}\[\^([^\]]+)]:\s*(.*)$/);
        if (!match) continue;

        const continuation: string[] = [match[2] ?? ""];
        let endLine = lineNumber;
        for (let cursor = lineNumber + 1; cursor <= doc.lines; cursor += 1) {
            if (isOccupied(cursor, excludedLines, occupiedLines)) break;
            const text = doc.line(cursor).text;
            if (!/^(?: {4,}|\t)/.test(text)) break;
            continuation.push(text.trim());
            endLine = cursor;
        }
        ranges.push({
            kind: "footnote-definition",
            startLine: lineNumber,
            endLine,
            fromIndex: lineLeadingSpaces(line.text),
            toIndex: line.text.length,
            raw: continuation.join("\n"),
            title: match[1],
            body: continuation.join(" ").trim(),
        });
        markOccupied(occupiedLines, lineNumber, endLine);
        lineNumber = endLine;
    }
    return ranges;
}

export function collectMarkdownRichBlockRanges(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number> = new Set(),
    occupiedInput: ReadonlySet<number> = new Set(),
): MarkdownRichBlockRange[] {
    doc = withCachedMarkdownDocLines(doc);
    const occupiedLines = new Set(occupiedInput);
    const ranges: MarkdownRichBlockRange[] = [];

    const frontmatter = collectMarkdownFrontmatterRange(doc, excludedLines);
    if (frontmatter) {
        ranges.push(frontmatter);
        markOccupied(occupiedLines, frontmatter.startLine, frontmatter.endLine);
    }

    ranges.push(...collectTableRanges(doc, excludedLines, occupiedLines));
    ranges.push(...collectFootnoteDefinitionRanges(doc, excludedLines, occupiedLines));
    ranges.push(...collectDefinitionListRanges(doc, excludedLines, occupiedLines));
    ranges.push(...collectBlockquoteRanges(doc, excludedLines, occupiedLines));

    return ranges.sort((left, right) =>
        left.startLine - right.startLine || left.endLine - right.endLine,
    );
}

export function rangeContainsMarkdownRichInline(
    ranges: readonly MarkdownRichInlineMatch[],
    index: number,
): boolean {
    return ranges.some((range) =>
        index >= range.fromIndex && index < range.toIndex,
    );
}

export function rangeContainsMarkdownRichBlock(
    ranges: readonly MarkdownRichBlockRange[],
    lineNumber: number,
): boolean {
    return ranges.some((range) =>
        lineNumber >= range.startLine && lineNumber <= range.endLine,
    );
}
